import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from '../../../../app/store'

export interface EditDistributionInstructionsState {
  instructionIds: number[]
  exchangeRate: string
  billingMonth: string | null
  selectedBillingMonth: number
  selectedBillingYear: number
  billingError: string | null
  csvFileSelected: boolean
  isCSVParsing: boolean
  csvFileParseSuccess: boolean
  distributeInstructionsEnabled: boolean
  cancelButtonsEnabled: boolean
}

const initialState: EditDistributionInstructionsState = {
  instructionIds: [],
  exchangeRate: '',
  billingMonth: null,
  selectedBillingMonth: 0,
  selectedBillingYear: 2023,
  billingError: '',
  csvFileSelected: false,
  isCSVParsing: false,
  csvFileParseSuccess: false,
  distributeInstructionsEnabled: false,
  cancelButtonsEnabled: false
}

export const editDistributionInstructionSlice = createSlice({
  name: 'editDistributionInstruction',
  initialState,
  reducers: {
    setInstructionIds: (state, action: PayloadAction<number[]>) => {
      state.instructionIds = action.payload
    },
    setExchangeRate: (state, action: PayloadAction<string>) => {
      state.exchangeRate = action.payload
    },
    setBillingMonth: (state, action: PayloadAction<string | null>) => {
      state.billingMonth = action.payload
    },
    setSelectedBillingMonth: (state, action: PayloadAction<number>) => {
      state.selectedBillingMonth = action.payload
    },
    setSelectedBillingYear: (state, action: PayloadAction<number>) => {
      state.selectedBillingYear = action.payload
    },
    setDistributeInstructionsEnabled: (state, action: PayloadAction<boolean>) => {
      state.distributeInstructionsEnabled = action.payload
    },
    setCancelButtonsEnabled: (state, action: PayloadAction<boolean>) => {
      state.cancelButtonsEnabled = action.payload
    },
    setCSVFileSelected: (state, action: PayloadAction<boolean>) => {
      state.csvFileSelected = action.payload
    },
    setCSVParsing: (state, action: PayloadAction<boolean>) => {
      state.isCSVParsing = action.payload
    },
    resetDistributionInstructions: (state) => {
      state.instructionIds = []
      state.exchangeRate = ''
      state.billingMonth = null
      state.selectedBillingMonth = 0
      state.selectedBillingYear = 2023
      state.distributeInstructionsEnabled = false
      state.cancelButtonsEnabled = false
      state.csvFileSelected = false
      state.isCSVParsing = false
    }
  }
})

export const {
  setInstructionIds,
  setExchangeRate,
  setBillingMonth,
  setSelectedBillingYear,
  setSelectedBillingMonth,
  setCancelButtonsEnabled,
  setDistributeInstructionsEnabled,
  setCSVFileSelected,
  setCSVParsing,
  resetDistributionInstructions
} = editDistributionInstructionSlice.actions

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectInstructionIds = (state: RootState): number[] => state.editDistributionInstruction.instructionIds
export const selectExchangeRate = (state: RootState): string => state.editDistributionInstruction.exchangeRate
export const selectBillingMonth = (state: RootState): string | null => state.editDistributionInstruction.billingMonth
export const selectSelectedBillingYear = (state: RootState): number => state.editDistributionInstruction.selectedBillingYear
export const selectSelectedBillingMonth = (state: RootState): number => state.editDistributionInstruction.selectedBillingMonth
export const selectDistributeInstructionsEnabled = (state: RootState): boolean => state.editDistributionInstruction.distributeInstructionsEnabled
export const selectCancelInstructionButtons = (state: RootState): boolean => state.editDistributionInstruction.cancelButtonsEnabled
export const selectCSVFileSelected = (state: RootState): boolean => state.editDistributionInstruction.csvFileSelected
export const selectIsCSVParsing = (state: RootState): boolean => state.editDistributionInstruction.isCSVParsing

export default editDistributionInstructionSlice.reducer
